import CheckoutModalPresenter from '@src/checkout/checkoutModalPresenter';
import CheckoutLessonPresenter from '@src/checkout/lesson/checkoutLessonPresenter';
import ModalLayout, {
  closeTimeoutMS,
  defaultModalStyles,
} from '@src/components/layout/modalLayout';
import { getUser } from '@src/services/auth';
import TeacherPagePresenter from '@src/teachers/teacherPagePresenter';
import api from '@src/utils/api';
import { Themed } from '@theme-ui/mdx';
import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/build/css/intlTelInput.css';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactModal from 'react-modal';
import { Box, Button, Field, Grid } from 'theme-ui';
import { useUserUpdate } from '../services/userContext';

function phoneNumberModal({ isOpen, onClose, location, teacher }) {
  const { t } = useTranslation();
  const user = getUser();
  const updateUser = useUserUpdate();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState(false);
  const telref = useRef(null);
  const [refVisible, setRefVisible] = useState(false);
  const [countryCodeInput, setCountryCodeInput] = useState(null);
  const EventBYRole = {
    2: 'Teacher',
    3: 'Student',
    4: 'Student representative',
  };
  const checkoutModalPresenter = new CheckoutModalPresenter();
  const checkoutLessonPresenter = new CheckoutLessonPresenter();
  const teacherPagePresenter = new TeacherPagePresenter();
  const googleRegistration = 'googleRegistration';
  const bookALesson = 'teacherCard';
  const teacherPage = 'teacherPage';
  let headerText;
  if (location === googleRegistration) {
    headerText = t('savePhone.completeRegistration');
  }
  if (location === bookALesson || location === teacherPage) {
    headerText = t('savePhone.bookALesson');
  }

  useEffect(() => {
    if (!refVisible) {
      return;
    }

    const input = telref.current.querySelector('input');

    const preferredCountries = [];
    if (process.env.GATSBY_DEFAULT_LANGUAGE) {
      preferredCountries.push(process.env.GATSBY_DEFAULT_LANGUAGE);
    }
    const ini = intlTelInput(input, {
      // any initialisation options go here
      preferredCountries: preferredCountries,
      separateDialCode: true,
      customPlaceholder: function (
        selectedCountryPlaceholder,
        selectedCountryData
      ) {
        return '61234567';
      },
      formatOnDisplay: false,
      customPlaceholder: () => '61234567',
      utilsScript:
        'https://cdn.jsdelivr.net/npm/intl-tel-input@18.2.1/build/js/utils.js',
    });

    // Format phone number on change
    input.addEventListener('input', () => {
      const a = ini.getSelectedCountryData();
      ini.setNumber(
        input.value?.startsWith('+')
          ? input.value
          : `+${a.dialCode}${input.value}`
      );
    });

    setCountryCodeInput(ini);
  }, [refVisible]);
  useEffect(() => {
    if (errors.phone) {
      setErrorMessage('');
    }
  }, [errors.phone]);
  const checkPhone = async (value) => {
    const isValidPhoneNumber = countryCodeInput.isValidNumber();
    if (!isValidPhoneNumber) {
      return t('invalidPhoneNumber');
    } else {
      const response = await api.post('auth/checkPhone', {
        phone: countryCodeInput.getNumber(intlTelInputUtils.numberFormat.E164),
      });
      if (!response) {
        return t('phoneAlreadyInUse');
      } else {
        return true;
      }
    }
  };
  function testPhone() {
    return countryCodeInput.isValidNumber()
      ? t('phoneAlreadyInUse')
      : !countryCodeInput.isValidNumber() && !countryCodeInput.telInput.value
      ? t('fieldRequired')
      : t('invalidPhoneNumber');
  }
  const openBookALessonModal = () => {
    teacherPagePresenter.bookALesson();
  };
  const onSubmit = async (data) => {
    const phone = countryCodeInput.getNumber(
      intlTelInputUtils.numberFormat.E164
    );
    const dto = {
      phone,
      userId: user?.id,
    };
    if (user && user?.id) {
      try {
        const response = await api.post('auth/savePhone', dto);
        if (response.status === 500) {
          if (response.errorCode === 'USER_NOT_FOUND') {
            return setErrorMessage(t('error.userNotFound'));
          } else {
            return setErrorMessage(t('error.unknownError'));
          }
        }
        if (response.message == 'success') {
          setSuccessMessage(true);
          updateUser({ ...user, complete: response?.complete });
          setTimeout(onClose, 1000);
          if (location === googleRegistration) {
            window.dataLayer.push({
              event: 'sign_up',
              method: 'Google',
              user_type: EventBYRole[user.role],
            });
            window.location.href = process.env.GATSBY_ADMIN_APP_URL;
          }
          if (location === bookALesson) {
            checkoutLessonPresenter.selectTeacher(teacher);
            checkoutModalPresenter.toggleModal({ type: 'lesson' });
          }
          if (location === teacherPage) {
            setTimeout(openBookALessonModal, 1000);
          }
        }
      } catch (error) {
        setErrorMessage(t('error.unknownError'));
      }
    } else {
      setErrorMessage(t('error.userNotFound'));
    }
  };
  return (
    <ReactModal
      isOpen={isOpen}
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      ariaHideApp={false}
      onRequestClose={onClose}
      closeTimeoutMS={closeTimeoutMS}
      style={defaultModalStyles}
    >
      <ModalLayout>
        <Box
          bg="white"
          sx={{
            position: 'relative',
            borderRadius: '10px',
            overflow: 'hidden',
            height: '100%',
          }}
        >
          <Button
            variant="empty"
            sx={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              fontSize: '14px',
              color: 'text',
            }}
            onClick={onClose}
          >
            {t('close')}
          </Button>

          <Grid columns={[1]} gap={0}>
            <Box px="60px" py={4}>
              <Themed.h3
                sx={{
                  textAlign: 'center',
                  fontSize: '28px',
                  maxWidth: ['100%', '30vw'],
                  margin: 'auto',
                  padding: '20px 0',
                }}
              >
                {headerText}
              </Themed.h3>
              <form
                onSubmit={handleSubmit(onSubmit)}
                sx={{ maxWidth: '350px', margin: 'auto' }}
              >
                <Box
                  className="labels-on-inputs"
                  ref={(el) => {
                    telref.current = el;
                    setRefVisible(!!el);
                  }}
                  sx={{ paddingBottom: 4 }}
                >
                  <Field
                    label={t('phone')}
                    name="phone"
                    type="tel"
                    className={errors.phone ? 'field-error' : ''}
                    {...register('phone', {
                      required: true,
                      validate: (val) => checkPhone(val),
                    })}
                  />
                  {errors.phone && (
                    <span sx={{ color: 'error' }}>{testPhone()}</span>
                  )}
                  {errorMessage && (
                    <span sx={{ color: 'error' }}>{errorMessage}</span>
                  )}
                  {successMessage && (
                    <div
                      sx={{
                        color: '#287a41',
                        mt: '10px',
                      }}
                    >
                      {t('phoneSavedSuccessfully')}
                    </div>
                  )}
                </Box>

                <Button className="w-100" type="submit">
                  {t('savePhoneNumber')}
                </Button>
              </form>
            </Box>
          </Grid>
        </Box>
      </ModalLayout>
    </ReactModal>
  );
}

export default phoneNumberModal;
